import type { ComponentProps } from 'react'

import Alert from '@components/atoms/Alert'

import type { ComponentDoc, PropDocs } from '../types'

type AlertProps = ComponentProps<typeof Alert>

const props: PropDocs<AlertProps> = {
  type: {
    type: "'info' | 'success' | 'warning' | 'error'",
    required: true,
    description: 'Sets the colour and the default icon.',
  },
  size: {
    type: "'sm' | 'md'",
    default: "'md'",
    description: "8px or 16px padding. 'sm' also sets small text, so plain-text children need no wrapper.",
  },
  title: { type: 'string', description: 'A bold first line above the children.' },
  children: { type: 'ReactNode', description: 'The message. Text, or text with a link.' },
  icon: {
    type: 'IconTypes',
    description: 'Replaces the default icon for the type, like search-lg for search results.',
  },
  onClose: { type: '() => void', description: 'Shows a dismiss button that calls it.' },
  className: { type: 'string', description: 'Added to the container.' },
  role: { type: "'alert'", description: 'Set it for an error that appears after the page loads.' },
  'data-testid': { type: 'string', description: 'Passed to the container.' },
}

const doc: ComponentDoc<AlertProps> = {
  slug: 'alert',
  name: 'Alert',
  tier: 'atom',
  description:
    'A tinted box with an icon that tells the user something about the page or the content below it. Use it for search results, warnings, errors and successes.',
  importStatement: "import { Alert } from '@atoms'",
  sourcePath: 'app/frontend/components/atoms/Alert.tsx',
  component: Alert,
  props,
  examples: [
    {
      title: 'Types',
      variants: [
        { type: 'info', children: 'Supporters added here join the group straight away.' },
        { type: 'success', children: 'The roster was published.' },
        { type: 'warning', children: 'This calling session has no supporters left to call.' },
        { type: 'error', children: 'The CSV could not be read.' },
      ],
    },
    {
      title: 'Search results',
      note: 'With the search icon, above the list it describes.',
      variants: [
        { type: 'info', icon: 'search-lg', children: 'Showing 1 group matching your search' },
        { type: 'info', icon: 'search-lg', children: 'No supporters found matching your search' },
      ],
    },
    {
      title: 'With a title',
      variants: [
        {
          type: 'warning',
          title: 'Sending is turned off',
          children: 'This is the demo site, so no messages are sent.',
        },
      ],
    },
    {
      title: 'Small',
      variants: [{ type: 'info', size: 'sm', children: 'Times are shown in the group’s time zone.' }],
    },
    {
      title: 'Dismissible',
      render: () => (
        <Alert type="success" onClose={() => {}}>
          Your changes were saved.
        </Alert>
      ),
      code: `<Alert type="success" onClose={() => setShowSaved(false)}>
  Your changes were saved.
</Alert>`,
    },
  ],
  usage: {
    do: [
      'Put it directly above the content it is about.',
      'Keep it to a sentence or two. Use the title for the one thing the user must not miss.',
      'Use the search-lg icon for search and filter results.',
    ],
    dont: [
      'Use it for a message that confirms an action the user just took. Use a toast.',
      'Stack several on one page. Combine them or pick the one that matters.',
    ],
  },
  a11y: [
    'It is not a live region. To announce one that appears after a search, render it inside a role="status" element that is always on the page.',
    'Pass role="alert" only for an error that appears after the page loads. It interrupts the screen reader.',
    'The icon is decorative. The text carries the meaning.',
    'The dismiss button is named "Dismiss", in the reader\'s language, so it never shares a name with a dialog\'s Close button.',
  ],
}

export default doc
